import { Plain } from '@/app/technical';
import { Icon, type Glyph } from './icons';
import { useTooltip } from './Tooltip';
import { forwardRef, useEffect, useRef, useState, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode } from 'react';

/**
 * The Norum primitives, from the component gallery.
 *
 * Rules that hold everywhere: square corners, a 1px rule instead of a
 * shadow, at least 44px on anything interactive, moss means state and never
 * decoration. Every part a screen uses exists here first.
 */

/**
 * The gallery's six (Component Gallery, `buttons`), plus the one solid red
 * button the prototype draws: STOP EVERY AGENT on Safety. `danger` itself is
 * an outline that fills on hover — it opens the confirm step, it is not it.
 * `quiet` is the gallery's ghost.
 */
export type Variant = 'primary' | 'secondary' | 'tertiary' | 'quiet' | 'danger' | 'danger-solid' | 'link';

const VARIANT: Record<Variant, string> = {
  primary:
    'bg-[var(--btn-bg)] text-[var(--btn-fg)] border-[var(--btn-bg)] hover:bg-[var(--btn-bg-hover)] hover:border-[var(--btn-bg-hover)]',
  secondary: 'bg-transparent text-ink border-strong hover:bg-strong hover:text-page',
  tertiary: 'bg-transparent text-muted border-transparent hover:text-ink',
  quiet: 'bg-transparent text-secondary border-control hover:border-strong hover:text-ink',
  danger: 'bg-transparent text-danger border-danger hover:bg-danger hover:text-[var(--danger-fg)]',
  'danger-solid': 'bg-danger text-[var(--danger-fg)] border-danger hover:opacity-90',
  link: 'bg-transparent text-accent border-transparent underline underline-offset-2 hover:text-ink',
};

export const Button = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: 'tap' | 'row'; icon?: Glyph; collapse?: boolean }
>(function Button({ variant = 'quiet', size = 'tap', icon, collapse, className = '', children, ...rest }, ref) {
  // `collapse`: on a phone only the glyph stays; the word is still read aloud and shows on long press.
  const { handlers, tipProps } = useTooltip(typeof children === 'string' ? children : '');
  const chained = collapse ? chain(handlers, rest as Record<string, unknown>) : {};
  return (
    <button
      ref={ref}
      type={rest.type ?? 'button'}
      className={`inline-flex items-center justify-center gap-2 rounded-brand border text-label uppercase tracking-button transition-colors duration-150 ease-brand disabled:opacity-40 active:translate-y-px ${
        // Both sizes are 44px on both axes (FRONTEND-AUDIT A2): `row` is
        // narrower padding for a row of actions, never a smaller target.
        size === 'tap' ? 'relative min-h-tap min-w-tap px-[22px]' : 'relative min-h-tap min-w-tap px-3'
      } ${VARIANT[variant]} ${className}`}
      {...rest}
      {...chained}
      {...(collapse && icon ? { ...tipProps, 'data-tip-phone': '' } : {})}
    >
      {icon && <Icon glyph={icon} size={14} />}
      {collapse && icon ? <span className="phone:sr-only">{children}</span> : children}
    </button>
  );
});

/**
 * The letter-spaced caps above a title or a group. `rail` is the smaller,
 * wider one the rail's groups use.
 */
export function Kicker({
  children,
  tone,
  size,
}: {
  children: ReactNode;
  tone?: 'danger' | 'accent' | undefined;
  size?: 'rail' | undefined;
}) {
  const color = tone === 'danger' ? 'text-danger' : tone === 'accent' ? 'text-accent' : 'text-meta';
  const scale = size === 'rail' ? 'text-micro tracking-rail' : 'text-micro tracking-label';
  return <span className={`font-medium uppercase ${scale} ${color}`}><Plain>{children}</Plain></span>;
}

/** Both handlers run: a menu trigger's own `onPointerDown` must not silence the tooltip's. */
function chain(ours: Record<string, (e: never) => void>, theirs: Record<string, unknown>) {
  const out: Record<string, (e: never) => void> = {};
  for (const [k, ours1] of Object.entries(ours)) {
    const theirs1 = theirs[k];
    out[k] =
      typeof theirs1 === 'function'
        ? (e) => {
            (theirs1 as (e: never) => void)(e);
            ours1(e);
          }
        : ours1;
  }
  return out;
}

/**
 * A square 44px control whose name is its label, never its glyph. The label
 * also shows on hover, focus and long press (`Tooltip.tsx`); `ghost` drops the
 * outline for a control that sits inside a row of its own.
 */
export const IconButton = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & {
    label: string;
    children?: ReactNode;
    glyph?: Glyph;
    ghost?: boolean;
    danger?: boolean;
  }
>(function IconButton({ label, children, glyph, ghost, danger, className = '', ...rest }, ref) {
  const { handlers, tipProps } = useTooltip(label);
  const chained = chain(handlers, rest as Record<string, unknown>);
  const tone = danger ? 'text-danger hover:border-danger' : 'text-secondary hover:border-strong hover:text-ink';
  const edge = ghost ? 'border-transparent' : 'border-control';
  return (
    <button
      ref={ref}
      type={rest.type ?? 'button'}
      aria-label={label}
      className={`relative inline-flex min-h-tap min-w-tap items-center justify-center gap-2 border ${edge} bg-transparent px-3 transition-colors duration-150 ease-brand disabled:opacity-40 ${tone} ${className}`}
      {...rest}
      {...chained}
      {...tipProps}
    >
      {glyph && <Icon glyph={glyph} />}
      {children}
    </button>
  );
});

/**
 * The header's outlined action: a glyph, and its word when the column has room.
 * Collapsed to the glyph, the word still shows on hover, focus and long press.
 */
export const HEADER_ACTION =
  // min-w-tap: an icon alone is 42px across at this padding, under the 44px a target needs.
  'relative inline-flex min-h-tap min-w-tap items-center justify-center gap-2 border border-control px-3 text-micro uppercase tracking-[0.12em] whitespace-nowrap text-secondary hover:border-accent hover:text-ink';

export const HeaderButton = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & { label: string; glyph: Glyph; word?: string | false | undefined }
>(function HeaderButton({ label, glyph, word, className = '', ...rest }, ref) {
  const { handlers, tipProps } = useTooltip(label);
  const chained = chain(handlers, rest as Record<string, unknown>);
  return (
    <button
      ref={ref}
      type={rest.type ?? 'button'}
      aria-label={label}
      className={`${HEADER_ACTION} ${className}`}
      {...rest}
      {...chained}
      {...tipProps}
    >
      <Icon glyph={glyph} />
      {word && <span>{word}</span>}
    </button>
  );
});

/**
 * An underlined action inside a sentence or a row. Same 44px floor as every
 * other control; the look is the caller's colour and size.
 */
export const TextAction = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement>>(function TextAction(
  { className = '', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={rest.type ?? 'button'}
      className={`inline-flex min-h-tap min-w-tap items-center justify-center px-1 underline underline-offset-[3px] hover:text-ink ${className}`}
      {...rest}
    />
  );
});

/**
 * On a table that scrolls sideways, keeps the first column (the name of the
 * row) in view. Put it on the `<table>`; it reads the `thead` and `tbody` it holds.
 */
export const STICK_FIRST =
  '[&_thead_tr>:first-child]:sticky [&_thead_tr>:first-child]:left-0 [&_thead_tr>:first-child]:z-[1] [&_thead_tr>:first-child]:bg-raised [&_tbody_tr>:first-child]:sticky [&_tbody_tr>:first-child]:left-0 [&_tbody_tr>:first-child]:z-[1] [&_tbody_tr>:first-child]:bg-page';

/** A row of actions that, on a phone, is one full-width button under another. */
export const STACK_ON_PHONE = 'phone:flex-col phone:items-stretch phone:[&>button]:w-full';

/** The submit of a composer: solid once there is something to send, quiet before. */
export function SendButton({ ready, disabled, children = 'Send' }: { ready: boolean; disabled: boolean; children?: ReactNode }) {
  return (
    <button
      type="submit"
      disabled={disabled}
      className={`inline-flex min-h-tap min-w-tap items-center justify-center gap-2 px-4 text-label uppercase tracking-button disabled:opacity-60 ${
        ready ? 'bg-[var(--btn-bg)] text-[var(--btn-fg)] hover:bg-[var(--btn-bg-hover)]' : 'bg-sunken text-meta'
      }`}
    >
      <Icon glyph="forward" size={14} />
      {children}
    </button>
  );
}

/** The yellow square with a number: something waits on a person. */
export function Count({ n, label }: { n: number; label?: string | undefined }) {
  if (n <= 0) return null;
  return (
    <span
      role={label ? 'img' : undefined}
      aria-label={label}
      className="inline-flex min-w-[18px] items-center justify-center bg-warning px-1 text-micro font-semibold leading-[18px] text-[var(--warning-fg)]"
    >
      {n > 99 ? '99+' : n}
    </span>
  );
}

export function Field({
  label,
  error,
  hint,
  children,
}: {
  label: string;
  error?: string | undefined;
  hint?: string | undefined;
  children: ReactNode;
}) {
  return (
    <label className="flex flex-col gap-2">
      <span className="text-micro font-medium uppercase tracking-label text-meta">{label}</span>
      {children}
      {error && <span className="text-dense text-danger">{error}</span>}
      {hint && !error && <span className="text-dense text-meta"><Plain>{hint}</Plain></span>}
    </label>
  );
}

export const TextInput = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement> & { invalid?: boolean }
>(function TextInput({ invalid, className = '', ...rest }, ref) {
  return (
    <input
      ref={ref}
      aria-invalid={invalid || undefined}
      className={`min-h-tap rounded-brand border bg-raised px-4 py-3 text-dense text-ink outline-none ${
        invalid ? 'border-danger' : 'border-control'
      } ${className}`}
      {...rest}
    />
  );
});

/**
 * The gallery's six badge rules: neutral says nothing is claimed; accent
 * says something is true; `current` (accent fill) is the current state,
 * exactly one per list; warning waits on a human; danger crosses a line;
 * `auto` (dashed) shipped without one.
 */
export type Tone = 'accent' | 'current' | 'warning' | 'danger' | 'neutral' | 'auto';

const TONE: Record<Tone, string> = {
  accent: 'border-solid border-accent text-accent bg-transparent',
  current: 'border-solid border-accent bg-accent text-accent-contrast',
  warning: 'border-solid border-warning bg-warning text-[var(--warning-fg)]',
  danger: 'border-solid border-danger bg-danger text-[var(--danger-fg)]',
  neutral: 'border-solid border-control text-secondary bg-transparent',
  auto: 'border-dashed border-control text-meta bg-transparent',
};

export function Badge({
  tone = 'neutral',
  plain,
  children,
}: {
  tone?: Tone;
  /** Sentence case at 11px, as Today's chips are ("Needs a reply", "Task"). */
  plain?: boolean | undefined;
  children: ReactNode;
}) {
  return (
    <span
      className={`inline-block whitespace-nowrap border ${
        plain ? 'px-[7px] py-[2px] text-label leading-[1.35]' : 'px-2 py-[3px] text-micro leading-[1.2] uppercase tracking-[0.08em]'
      } ${TONE[tone]}`}
    >
      {children}
    </span>
  );
}

/**
 * A person is initials in a ring; an agent is the same with a moss square on
 * the corner — the one thing that distinguishes them, and it carries what
 * the agent is (§7). A twin's square carries whose it is.
 */
export function Avatar({
  name,
  size = 28,
  agent,
  state,
}: {
  name: string;
  size?: number;
  agent?: { label: string } | undefined;
  /** `unknown`: nobody the platform knows (a dashed ring and a ?). `suspended`: the mark turns red. */
  state?: 'unknown' | 'suspended' | undefined;
}) {
  const initials = name
    .split(/[\s.·]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
  const mark = Math.max(9, Math.round(size * 0.3));
  return (
    <span className="relative block shrink-0" style={{ width: size, height: size }} aria-hidden>
      <span
        className={`flex items-center justify-center rounded-full border font-semibold text-secondary ${
          state === 'unknown' ? 'border-dashed border-control bg-transparent' : 'border-line bg-sunken'
        }`}
        style={{ width: size, height: size, fontSize: Math.max(10.5, Math.round(size * 0.34)) }}
      >
        {state === 'unknown' ? '?' : initials}
      </span>
      {(agent || state === 'suspended') && (
        <span
          title={agent?.label ?? 'Suspended'}
          className={`absolute -bottom-0.5 -right-0.5 block border-2 border-page ${state === 'suspended' ? 'bg-danger' : 'bg-accent'}`}
          style={{ width: mark, height: mark }}
        />
      )}
    </span>
  );
}

export function Skeleton({ rows = 6 }: { rows?: number }) {
  const widths = [78, 62, 70, 55, 66, 72, 58, 68];
  return (
    <div className="flex flex-col gap-px border border-line bg-line" aria-hidden>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="bg-page p-4">
          <div
            className="h-3 animate-[npulse_1.6s_ease-in-out_infinite] bg-[var(--skeleton)]"
            style={{ width: `${widths[i % widths.length]}%` }}
          />
        </div>
      ))}
    </div>
  );
}

export function EmptyState({
  title,
  body,
  action,
  icon,
}: {
  title: string;
  body: string;
  action?: { label: string; onClick: () => void } | undefined;
  /** What the empty thing is, as a glyph; a plain square when it is not said. */
  icon?: Glyph | undefined;
}) {
  return (
    <div className="flex flex-col items-center gap-3 border border-dashed border-control px-6 py-14 text-center">
      {icon ? (
        <span className="text-meta">
          <Icon glyph={icon} size={22} />
        </span>
      ) : (
        <div className="h-5 w-5 border-[1.25px] border-control" />
      )}
      <p className="m-0 text-body font-semibold">{title}</p>
      <p className="m-0 max-w-[38ch] text-dense text-muted">{body}</p>
      {action && (
        <Button variant="secondary" className="mt-1.5" onClick={action.onClick}>
          {action.label}
        </Button>
      )}
    </div>
  );
}

/**
 * The gallery's Segment (G:197-203): two or three options, underlined, not
 * filled — for choosing a view, never for choosing an action.
 */
export function Segment<V extends string>({
  label,
  value,
  options,
  onChoose,
}: {
  label: string;
  value: V;
  options: ReadonlyArray<{ value: V; label: string }>;
  onChoose: (v: V) => void;
}) {
  return (
    <div role="radiogroup" aria-label={label} className="flex border-b border-line">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={o.value === value}
          onClick={() => onChoose(o.value)}
          className={`-mb-px min-h-tap flex-1 border-b-2 px-3 text-label uppercase tracking-label ${
            o.value === value ? 'border-accent text-ink' : 'border-transparent text-meta hover:text-ink'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** The gallery's Switch (G:205-209): a square knob on a 44px target, and its sentence beside it. */
export function Switch({ checked, onChange, label }: { checked: boolean; onChange: (next: boolean) => void; label: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className="inline-flex min-h-tap items-center gap-3 text-left text-dense"
    >
      <span
        aria-hidden
        className={`flex h-[22px] w-[40px] shrink-0 items-center border p-[3px] ${
          checked ? 'justify-end border-accent bg-accent' : 'justify-start border-control bg-sunken'
        }`}
      >
        <span className={`block h-[14px] w-[14px] ${checked ? 'bg-accent-contrast' : 'bg-[var(--border-control)]'}`} />
      </span>
      {label}
    </button>
  );
}

/**
 * The gallery's HoldButton (G:243-248): on a touch screen an irreversible
 * act is a press held for a second and a half, filling as it goes, rather
 * than a tap on a confirm that a thumb can hit by accident. Letting go
 * early does nothing.
 */
export function HoldButton({ label, holding, done, onHeld }: { label: string; holding: string; done?: string; onHeld: () => void }) {
  const [pct, setPct] = useState(0);
  const timer = useRef<number | null>(null);
  const started = useRef(0);
  const stop = () => {
    if (timer.current !== null) window.clearInterval(timer.current);
    timer.current = null;
    setPct((p) => (p >= 100 ? p : 0));
  };
  const start = () => {
    if (timer.current !== null) return;
    started.current = Date.now();
    timer.current = window.setInterval(() => {
      const p = Math.min(100, ((Date.now() - started.current) / 1500) * 100);
      setPct(p);
      if (p >= 100) {
        if (timer.current !== null) window.clearInterval(timer.current);
        timer.current = null;
        onHeld();
      }
    }, 30);
  };
  useEffect(() => stop, []);
  return (
    <button
      type="button"
      onPointerDown={(e) => {
        e.preventDefault();
        start();
      }}
      onPointerUp={stop}
      onPointerLeave={stop}
      onPointerCancel={stop}
      onKeyDown={(e) => {
        if ((e.key === ' ' || e.key === 'Enter') && !e.repeat) start();
      }}
      onKeyUp={stop}
      className="relative min-h-12 w-full overflow-hidden border border-danger px-[26px] text-label uppercase tracking-button text-danger"
    >
      <span aria-hidden className="absolute inset-y-0 left-0 bg-danger" style={{ width: `${pct}%` }} />
      <span className={`relative ${pct > 50 ? 'text-[var(--danger-fg)]' : ''}`}>
        {pct >= 100 ? (done ?? label) : pct > 0 ? holding : label}
      </span>
    </button>
  );
}
